import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { lotesService } from '../../services/lotesService';
import { useToast } from '../../hooks/useToast';
import { useOrganizador } from '../../contexts/OrganizadorContext';

const Racas = () => {
  const { organizadorAtivo } = useOrganizador();
  const toast = useToast();
  
  const [racas, setRacas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRaca, setEditingRaca] = useState(null);
  const [formData, setFormData] = useState({ nome: '' });
  const [saving, setSaving] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (organizadorAtivo?.id) {
      carregarRacas(1, search);
    }
  }, [organizadorAtivo, search]);
  
  useEffect(() => {
    if (organizadorAtivo?.id) {
      carregarRacas(currentPage, search);
    }
  }, [currentPage]);

  const carregarRacas = async (page = 1, searchTerm = '') => {
    try {
      setLoading(true);
      const data = await lotesService.listarRacas(organizadorAtivo.id);
      let filteredData = data || [];
      
      // Filtrar por busca
      if (searchTerm) {
        filteredData = filteredData.filter(raca => 
          raca.nome.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      // Calcular paginação
      const total = filteredData.length;
      const totalPgs = Math.ceil(total / itemsPerPage);
      const startIndex = (page - 1) * itemsPerPage;
      const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage);
      
      setRacas(paginatedData);
      setTotalPages(totalPgs);
      setCurrentPage(page);
    } catch (error) {
      console.error('Erro ao carregar raças:', error);
      setRacas([]);
      toast.error('Erro ao carregar raças');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = (raca) => {
    const acao = raca.ativo ? 'inativar' : 'ativar';
    setConfirmAction({
      type: 'toggle',
      raca,
      message: `Tem certeza que deseja ${acao} a raça "${raca.nome}"?`,
      action: async () => {
        try {
          const result = await lotesService.alternarStatusRaca(organizadorAtivo.id, raca.id);
          toast.success(result.message);
          carregarRacas(currentPage, search);
        } catch (error) {
          console.error('Erro ao alterar status:', error);
          toast.error('Erro ao alterar status da raça');
        }
      }
    });
  };

  const handleDelete = (raca) => {
    setConfirmAction({
      type: 'delete',
      raca,
      message: `ATENÇÃO: Excluir PERMANENTEMENTE a raça "${raca.nome}"?`,
      subtitle: 'Esta ação não pode ser desfeita!',
      action: async () => {
        try {
          const result = await lotesService.excluirRaca(organizadorAtivo.id, raca.id);
          toast.success(result.message);
          carregarRacas(1, search);
        } catch (error) {
          console.error('Erro ao excluir raça:', error);
          const message = error.response?.data?.detail || 'Erro ao excluir raça';
          toast.error(message);
        }
      }
    });
  };

  const executeConfirmAction = async () => {
    if (confirmAction?.action) {
      await confirmAction.action();
    }
    setConfirmAction(null);
  };

  const cancelConfirmAction = () => {
    setConfirmAction(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }

    setSaving(true);
    try {
      if (editingRaca) {
        await lotesService.atualizarRaca(organizadorAtivo.id, editingRaca.id, { nome: formData.nome.trim() });
        toast.success('Raça atualizada com sucesso!');
      } else {
        await lotesService.criarRaca(organizadorAtivo.id, { nome: formData.nome.trim() });
        toast.success('Raça criada com sucesso!');
      }
      
      fecharModal();
      carregarRacas(1, search);
    } catch (error) {
      console.error('Erro ao salvar raça:', error);
      toast.error('Erro ao salvar raça');
    } finally {
      setSaving(false);
    }
  };

  const abrirModal = (raca = null) => {
    if (raca) {
      setEditingRaca(raca);
      setFormData({ nome: raca.nome });
    } else {
      setEditingRaca(null);
      setFormData({ nome: '' });
    }
    setShowModal(true);
  };

  const fecharModal = () => {
    setShowModal(false);
    setEditingRaca(null);
    setFormData({ nome: '' });
  };

  if (loading || !organizadorAtivo) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Raças</h1>
          <p className="text-gray-600 mt-1">Gerencie as raças de gado</p>
        </div>
        <button
          onClick={() => abrirModal()}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <Plus size={20} />
          Nova Raça
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg border p-4">
        <input
          type="text"
          placeholder="Buscar raças..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-lg border overflow-hidden">
        {racas.length > 0 ? (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data de Criação
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {racas.map((raca) => (
                <tr key={raca.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className={`font-medium ${
                        raca.ativo ? 'text-gray-900' : 'text-gray-400 line-through'
                      }`}>
                        {raca.nome}
                      </div>
                      {!raca.ativo && (
                        <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                          Inativo
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(raca.created_at).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-1">
                      <button
                        onClick={() => abrirModal(raca)}
                        className="text-blue-600 hover:text-blue-800 p-1 rounded transition-colors"
                        title="Editar"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleToggleStatus(raca)}
                        className={`p-1 rounded transition-colors ${
                          raca.ativo 
                            ? 'text-yellow-600 hover:text-yellow-800' 
                            : 'text-green-600 hover:text-green-800'
                        }`}
                        title={raca.ativo ? 'Inativar' : 'Ativar'}
                      >
                        {raca.ativo ? '⏸️' : '▶️'}
                      </button>
                      <button
                        onClick={() => handleDelete(raca)}
                        className="text-red-600 hover:text-red-800 p-1 rounded transition-colors"
                        title="Excluir permanentemente"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-500 mb-4">Nenhuma raça cadastrada</div>
            <p className="text-sm text-gray-400 mb-6">
              Cadastre as raças de gado para classificar os animais nos lotes
            </p>
            <button
              onClick={() => abrirModal()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Cadastrar Primeira Raça
            </button>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white rounded-lg border p-4 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            Página {currentPage} de {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Anterior
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-1 border rounded ${
                  currentPage === page 
                    ? 'bg-blue-600 text-white border-blue-600' 
                    : 'hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Próxima
            </button>
          </div>
        </div>
      )}

      {/* Toast Messages */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toast.toasts.map((t) => (
          <div
            key={t.id}
            className={`px-4 py-2 rounded-lg shadow-lg text-white ${
              t.type === 'success' ? 'bg-green-500' :
              t.type === 'error' ? 'bg-red-500' :
              t.type === 'warning' ? 'bg-yellow-500' :
              'bg-blue-500'
            }`}
          >
            {t.message}
          </div>
        ))}
      </div>

      {/* Confirmation Modal */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className={`text-xl font-semibold mb-4 ${
              confirmAction.type === 'delete' ? 'text-red-600' : 'text-yellow-600'
            }`}>
              {confirmAction.type === 'delete' ? '⚠️ Confirmar Exclusão' : '❓ Confirmar Ação'}
            </h2>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-2">{confirmAction.message}</p>
              {confirmAction.subtitle && (
                <p className="text-red-600 text-sm font-medium">{confirmAction.subtitle}</p>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={cancelConfirmAction}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={executeConfirmAction}
                className={`px-4 py-2 text-white rounded-lg transition-colors ${
                  confirmAction.type === 'delete' 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-yellow-600 hover:bg-yellow-700'
                }`}
              >
                {confirmAction.type === 'delete' ? 'Excluir' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">
              {editingRaca ? 'Editar Raça' : 'Nova Raça'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome da Raça *
                </label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({ nome: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: Nelore, Angus, Brahman..."
                  required
                  disabled={saving}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Exemplos: "Nelore", "Angus", "Brahman", "Senepol"
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={fecharModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
                  disabled={saving}
                >
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Racas;